import React, { useState } from "react";
import PropTypes from "prop-types";
import { Button, TextInput } from "@aqar-app/aqar-design-system";
import { useMutation } from "@apollo/client";
import { CREATE_PHONE, VERIFY } from "../../graphql/mutations/auth";
import { getDeviceID, isSaudiNumber, isValidPassword } from "../../lib/helpers";
import useForm from "../../hooks/useForm";
import FieldError from "../../components/field-error/FieldError";
import { setLoginCookies } from "../../lib/cookiesHelpers";
import UserInfo from "../userActions/UserInfo";
import styles from "./auth.module.scss";
import { trackSignupEvent } from "@/lib/tracking/trackAuthenticationEvents";

const rigisterPhoneValidator = (values) => {
  const errors = {};
  if (!values.phone || !isSaudiNumber(values.phone)) {
    errors.phone = "مطلوب رقم جوال سعودي";
  }
  return errors;
};

const verifyAccountValidator = (values) => {
  const errors = {};
  const { phone, pw, vcode } = values;
  if (!phone || !isSaudiNumber(phone)) {
    errors.phone = "مطلوب رقم جوال سعودي";
  }
  if (!pw || !isValidPassword(pw)) {
    errors.pw = [];
    if (!pw) {
      errors.pw.push("مطلوب كلمة المرور.");
    } else {
      if (pw.length < 8) {
        errors.pw.push("كلمة المرور يجب أن تحتوي على ثمانية أحرف على الآقل.");
      }
      if (!new RegExp(/^(?=.*[0-9])/).test(pw)) {
        errors.pw.push("كلمة المرور يجب أن تحتوي على رقم على الأقل.");
      }
      if (!new RegExp(/^(?=.*[A-z])/).test(pw)) {
        errors.pw.push("كلمة المرور يجب أن تحتوي على حرف واحد على الأقل.");
      }
    }
  }

  if (!vcode) {
    errors.vcode = "الرجاء إدخال رمز التحقق.";
  }
  return errors;
};

const SignUpCode = ({ redirectPostLogin }) => {
  const [mutationError, setMutationError] = useState(null);
  const [phoneRigestered, setPhoneRigestered] = useState(false);
  const [loginToken, setLoginToken] = useState(undefined);
  const [rigesterPhone, { loading }] = useMutation(CREATE_PHONE, {
    onError: (err) => {
      setMutationError({ phone: err.message });
    },
    onCompleted: () => {
      setPhoneRigestered(true);
      setMutationError(null);
      trackSignupEvent();
    },
  });

  const [verifyAccount, { verifyLoading }] = useMutation(VERIFY, {
    onError: (err) => {
      setMutationError({ vcode: err.message });
    },
    onCompleted: ({ User }) => {
      const { token, user } = User.verify;
      setLoginCookies(token, user);
      setLoginToken(token);
    },
  });

  const { values, errors, handleChange, handleSubmit } = useForm(
    // eslint-disable-next-line no-use-before-define
    submit,
    phoneRigestered ? verifyAccountValidator : rigisterPhoneValidator,
  );

  function submit() {
    if (phoneRigestered) {
      return verifyAccount({
        variables: { ...values, device_id: getDeviceID() },
      });
    }
    return rigesterPhone({ variables: values });
  }

  const isFieldInvalid = (field) => {
    return !!errors[field] || (mutationError && mutationError[field]);
  };

  const getFieldError = (field) => {
    return errors[field] || mutationError[field];
  };

  return (
    <>
      {loginToken ? (
        <UserInfo handleSuccess={redirectPostLogin} loginToken={loginToken} />
      ) : (
        <div>
          <div className={styles.label}>رقم الجوال</div>
          <div>
            <TextInput
              name="phone"
              disabled={phoneRigestered}
              placeholder="الرجاء كتابة رقم الجوال..."
              value={values.phone || ""}
              invalid={isFieldInvalid("phone")}
              inputMode="tel"
              type="number"
              onChange={handleChange}
              onPressEnter={handleSubmit}
              maxLength={14}
            />
            {isFieldInvalid("phone") && (
              <FieldError text={getFieldError("phone")} />
            )}
          </div>
          {!phoneRigestered ? null : (
            <div>
              <div className={styles.label}>رمز التحقق</div>
              <div>
                <TextInput
                  placeholder="أدخل الرمز هنا"
                  name="vcode"
                  invalid={isFieldInvalid("vcode")}
                  type="number"
                  onChange={handleChange}
                  inputMode="number"
                  onPressEnter={handleSubmit}
                />
                {isFieldInvalid("vcode") && (
                  <FieldError text={getFieldError("vcode")} />
                )}
              </div>
              <div className={styles.label}>كلمة المرور</div>
              <div>
                <TextInput.PasswordInput
                  placeholder="الرجاء كتابة كلمة المرور..."
                  name="pw"
                  invalid={isFieldInvalid("pw")}
                  onChange={handleChange}
                  onPressEnter={handleSubmit}
                />
                {isFieldInvalid("pw") && (
                  <ul className={styles.errorsList}>
                    {getFieldError("pw")?.map((error) => (
                      <li>
                        <FieldError text={error} />
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}
          <Button
            className={styles.actionButton}
            contentClassName={styles.content}
            label={phoneRigestered ? "تسجيل" : "إرسال رمز التحقق"}
            priority="primary"
            disabled={phoneRigestered ? verifyLoading : loading}
            onClick={handleSubmit}
          />
        </div>
      )}
    </>
  );
};

SignUpCode.propTypes = {
  redirectPostLogin: PropTypes.func.isRequired,
};

export default SignUpCode;
